/**
 * PortalSidebar — the ONE sidebar used by every SEPL portal.
 *
 * This file is copied verbatim into each portal (src/components/portal/PortalSidebar.tsx).
 * Do not edit a single copy: change the master and re-sync all portals so every
 * portal keeps the exact same size, spacing, colours and animation.
 *
 * All styling lives in the CSS string below (plain px values, own class names),
 * so differences between the portals' Tailwind configs / versions cannot change
 * how the sidebar looks. The only runtime deps are react and react-dom.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  ComponentType,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  Ref,
} from 'react';
import { createPortal } from 'react-dom';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PortalIcon = ComponentType<any>;

const COLLAPSE_KEY = 'portal.sidebar.collapsed';
const MOBILE_TOPBAR_H = 56;

export function readPortalSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writePortalSidebarCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  } catch {
    // ignore storage errors
  }
}

/** Expanded/collapsed (persisted, shared by every portal) + mobile drawer state. */
export function usePortalSidebar() {
  const [expanded, setExpandedState] = useState<boolean>(() => !readPortalSidebarCollapsed());
  const [mobileOpen, setMobileOpen] = useState(false);
  const setExpanded = (next: boolean | ((current: boolean) => boolean)) => {
    setExpandedState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      writePortalSidebarCollapsed(!value);
      return value;
    });
  };
  return { expanded, setExpanded, mobileOpen, setMobileOpen };
}

/** Props to spread on the page's main content element so it sits next to the rail. */
export function portalMainProps(expanded: boolean) {
  return { 'data-psb-main': expanded ? 'expanded' : 'collapsed' } as const;
}

/* ------------------------------------------------------------------ icons */

type SvgProps = { size?: number; className?: string; style?: CSSProperties };

function svg(children: ReactNode) {
  return function Icon({ size = 20, className, style }: SvgProps) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={style}
        aria-hidden="true"
      >
        {children}
      </svg>
    );
  };
}

export const HomeIcon = svg(
  <>
    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
    <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </>
);
export const BellIcon = svg(
  <>
    <path d="M10.268 21a2 2 0 0 0 3.464 0" />
    <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </>
);
const LogOutIcon = svg(
  <>
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
  </>
);
const PanelCloseIcon = svg(
  <>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M9 3v18" />
    <path d="m16 15-3-3 3-3" />
  </>
);
const PanelOpenIcon = svg(
  <>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M9 3v18" />
    <path d="m14 9 3 3-3 3" />
  </>
);
const CloseIcon = svg(
  <>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </>
);
const MenuIcon = svg(
  <>
    <path d="M4 12h16" />
    <path d="M4 6h16" />
    <path d="M4 18h16" />
  </>
);
const ChevronDownIcon = svg(<path d="m6 9 6 6 6-6" />);

/* ------------------------------------------------------------------ styles */

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif";

const CSS = `
.psb, .psb *, .psb-tip, .psb-tip * { box-sizing: border-box; }
.psb { font-family: ${FONT}; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; font-feature-settings: 'cv11', 'ss01'; }
.psb button { font: inherit; color: inherit; background: none; border: 0; margin: 0; padding: 0; cursor: pointer; text-align: left; letter-spacing: inherit; text-transform: none; -webkit-appearance: none; appearance: none; }
.psb p { margin: 0; }
.psb a { color: inherit; text-decoration: none; }
.psb svg { display: block; flex-shrink: 0; }
.psb button:focus { outline: none; }
.psb button:focus-visible, .psb a:focus-visible { outline: 2px solid rgba(255,255,255,.7); outline-offset: 2px; }

.psb-rail { position: fixed; top: 0; bottom: 0; left: 0; z-index: 40; display: none; width: 256px; transition: width 300ms ${EASE}; }
.psb-rail[data-collapsed='true'] { width: 56px; }
@media (min-width: 1024px) { .psb-rail { display: block; } }

.psb .psb-panel { display: flex; height: 100%; flex-direction: column; overflow: hidden; background-image: linear-gradient(to bottom, #2563eb, #1d4ed8, #1e3a8a); color: #fff; font-size: 14px; line-height: 20px; }
.psb .psb-panel[data-clickable='true'] { cursor: pointer; }

.psb .psb-head { display: flex; flex-shrink: 0; align-items: center; gap: 8px; border-bottom: 1px solid rgba(255,255,255,.1); padding: 16px 8px; min-height: 69px; }
.psb .psb-slot { display: flex; width: 40px; flex-shrink: 0; align-items: center; justify-content: center; }
.psb .psb-slot svg { width: 20px; height: 20px; }
.psb .psb-plate { position: relative; display: flex; width: 36px; height: 36px; align-items: center; justify-content: center; border-radius: 6px; background: #fff; color: #1d4ed8; box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1); transition: background-color 150ms ${EASE}; }
.psb button.psb-plate:hover { background: #eff6ff; }
.psb .psb-plate .psb-brand-icon { transition: opacity 150ms ${EASE}; }
.psb .psb-plate .psb-swap { position: absolute; inset: 0; margin: auto; opacity: 0; transition: opacity 150ms ${EASE}; }
.psb button.psb-plate:hover .psb-brand-icon { opacity: 0; }
.psb button.psb-plate:hover .psb-swap { opacity: 1; }
.psb .psb-brand { min-width: 0; flex: 1; }
.psb .psb-eyebrow { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; line-height: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: .22em; color: #bfdbfe; }
.psb .psb-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; line-height: 17.5px; font-weight: 600; }
.psb .psb-iconbtn { margin-right: 4px; flex-shrink: 0; border-radius: 4px; padding: 6px; color: #dbeafe; transition: color 150ms ${EASE}, background-color 150ms ${EASE}; }
.psb .psb-iconbtn:hover { background: rgba(255,255,255,.1); color: #fff; }
.psb .psb-iconbtn svg { width: 18px; height: 18px; }
.psb .psb-iconbtn.psb-close svg { width: 20px; height: 20px; }

.psb .psb-nav { margin-top: 12px; display: flex; min-height: 0; flex: 1; flex-direction: column; gap: 4px; overflow-x: hidden; overflow-y: auto; padding: 0 8px 8px; scrollbar-width: none; -ms-overflow-style: none; }
.psb .psb-nav::-webkit-scrollbar { display: none; width: 0; height: 0; }

.psb .psb-item { position: relative; display: flex; width: 100%; flex-shrink: 0; align-items: center; gap: 8px; border-radius: 6px; padding: 10px 12px 10px 0; font-size: 14px; line-height: 20px; font-weight: 600; color: #eff6ff; transition: color 150ms ${EASE}, background-color 150ms ${EASE}, box-shadow 150ms ${EASE}; }
.psb .psb-item:hover { background: rgba(255,255,255,.1); color: #fff; }
.psb .psb-item[data-active='true'] { background: #fff; color: #1d4ed8; box-shadow: 0 10px 15px -3px rgba(23,37,84,.2), 0 4px 6px -4px rgba(23,37,84,.2); }
.psb .psb-item[data-collapsed='true'] { padding-right: 0; }
.psb .psb-item.psb-logout:hover { background: rgba(244,63,94,.3); color: #eff6ff; }
.psb .psb-label { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.psb .psb-badge { margin-left: auto; flex-shrink: 0; border-radius: 9999px; background: #ef4444; padding: 2px 6px; font-size: 11px; font-weight: 700; line-height: 11px; color: #fff; }
.psb .psb-dotwrap { position: relative; }
.psb .psb-dot { position: absolute; right: -6px; top: -6px; display: flex; height: 16px; min-width: 16px; align-items: center; justify-content: center; border-radius: 9999px; background: #ef4444; padding: 0 4px; font-size: 10px; font-weight: 700; line-height: 10px; color: #fff; }
.psb .psb-chev { margin-left: auto; width: 16px; height: 16px; transition: transform 300ms ${EASE}; }
.psb .psb-chev[data-open='true'] { transform: rotate(180deg); }
.psb .psb-subwrap { flex-shrink: 0; overflow: hidden; max-height: 0; transition: max-height 300ms ease-in-out; }
.psb .psb-subwrap[data-open='true'] { max-height: 640px; }
.psb .psb-sub { margin-left: 16px; display: flex; flex-direction: column; gap: 4px; border-left: 1px solid rgba(255,255,255,.15); padding: 4px 0 0 8px; }
.psb .psb-section { flex-shrink: 0; padding: 12px 12px 4px; font-size: 10px; line-height: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: .18em; color: rgba(191,219,254,.7); }
.psb .psb-divider { flex-shrink: 0; margin: 8px 0; border-top: 1px solid rgba(255,255,255,.1); }

.psb .psb-foot { flex-shrink: 0; border-top: 1px solid rgba(255,255,255,.1); padding: 12px 8px; }
.psb .psb-user { margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.psb .psb-avatar { display: flex; width: 36px; height: 36px; align-items: center; justify-content: center; border-radius: 9999px; background: rgba(255,255,255,.15); font-size: 14px; line-height: 20px; font-weight: 700; }
.psb .psb-who { min-width: 0; flex: 1; }
.psb .psb-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; line-height: 17.5px; font-weight: 600; }
.psb .psb-email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; line-height: 16.5px; color: #bfdbfe; }
.psb .psb-extra { margin-bottom: 8px; }

.psb-topbar { position: fixed; top: 0; left: 0; right: 0; z-index: 30; display: flex; height: ${MOBILE_TOPBAR_H}px; align-items: center; gap: 12px; border-bottom: 1px solid #dbeafe; background: rgba(255,255,255,.95); -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px); padding: 0 16px; color: #0f172a; }
@media (min-width: 1024px) { .psb-topbar { display: none; } }
.psb .psb-topbtn { border-radius: 4px; padding: 8px; color: #0f172a; transition: background-color 150ms ${EASE}; }
.psb .psb-topbtn:hover { background: #eff6ff; }
.psb .psb-topbtn svg { width: 20px; height: 20px; }
.psb .psb-topbrand { display: flex; min-width: 0; align-items: center; gap: 8px; }
.psb .psb-topplate { display: flex; width: 32px; height: 32px; flex-shrink: 0; align-items: center; justify-content: center; border-radius: 4px; background: #1d4ed8; color: #fff; }
.psb .psb-topplate svg { width: 16px; height: 16px; }
.psb .psb-toptitle { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; line-height: 20px; font-weight: 700; }
.psb .psb-topright { margin-left: auto; display: flex; align-items: center; gap: 4px; }

.psb-drawer { position: fixed; inset: 0; z-index: 50; pointer-events: none; }
.psb-drawer[data-open='true'] { pointer-events: auto; }
@media (min-width: 1024px) { .psb-drawer { display: none; } }
.psb .psb-overlay { position: absolute; inset: 0; background: rgba(15,23,42,.5); opacity: 0; transition: opacity 300ms ${EASE}; }
.psb-drawer[data-open='true'] .psb-overlay { opacity: 1; }
.psb .psb-sheet { position: absolute; top: 0; bottom: 0; left: 0; width: 288px; max-width: 85vw; box-shadow: 0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1); transform: translateX(-100%); transition: transform 300ms cubic-bezier(0, 0, 0.2, 1); }
.psb-drawer[data-open='true'] .psb-sheet { transform: translateX(0); }

[data-psb-main] { transition: margin-left 300ms ${EASE}; }
@media (max-width: 1023.98px) { [data-psb-main] { margin-top: ${MOBILE_TOPBAR_H}px !important; } }
@media (min-width: 1024px) {
  [data-psb-main='expanded'] { margin-left: 256px !important; }
  [data-psb-main='collapsed'] { margin-left: 56px !important; }
}
@media print {
  .psb { display: none !important; }
  [data-psb-main] { margin: 0 !important; }
}

.psb-tip { position: fixed; z-index: 70; pointer-events: none; transform: translateY(-50%); font-family: ${FONT}; }
.psb-tip > span { display: block; animation: psb-tip-in .1s ease-out; border-radius: 3px; background: #0f172a; padding: 6px 10px; font-size: 12px; line-height: 16px; font-weight: 500; color: #fff; white-space: nowrap; box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1); }
@keyframes psb-tip-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
`;

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,400;14..32,500;14..32,600;14..32,700&display=swap';

function useInterFont() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.querySelector('link[data-psb-font]')) return;
    if (document.querySelector(`link[href^="https://fonts.googleapis.com/css2?family=Inter"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    link.setAttribute('data-psb-font', '1');
    document.head.appendChild(link);
  }, []);
}

/* ------------------------------------------------------------------ context */

type PanelCtx = {
  expanded: boolean;
  isMobile: boolean;
  expandRail: () => void;
  closeMobile: () => void;
};

const PanelContext = createContext<PanelCtx>({
  expanded: true,
  isMobile: false,
  expandRail: () => undefined,
  closeMobile: () => undefined,
});

export function usePortalSidebarPanel() {
  return useContext(PanelContext);
}

/* ------------------------------------------------------------------ tooltip */

function Tip({ anchor, label }: { anchor: DOMRect | null; label: string }) {
  if (!anchor || typeof document === 'undefined') return null;
  return createPortal(
    <div className="psb-tip" style={{ top: anchor.top + anchor.height / 2, left: anchor.right + 6 }}>
      <span>{label}</span>
    </div>,
    document.body
  );
}

function useTip(enabled: boolean) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!enabled) setAnchor(null);
  }, [enabled]);
  const show = (event: { currentTarget: Element }) => {
    if (enabled) setAnchor(event.currentTarget.getBoundingClientRect());
  };
  const hide = () => setAnchor(null);
  return { anchor, handlers: { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide } };
}

/* ------------------------------------------------------------------ items */

export type SidebarItemProps = {
  icon: PortalIcon;
  label: string;
  active?: boolean;
  badge?: number;
  onClick?: () => void;
  /** Render as a link instead of a button. */
  href?: string;
  /** Extra content at the end of the row (expanded only). */
  trailing?: ReactNode;
  buttonRef?: Ref<HTMLButtonElement>;
  ariaExpanded?: boolean;
  /** Keep the mobile drawer open after clicking (e.g. group toggles). */
  keepOpen?: boolean;
};

export function SidebarItem({
  icon: Icon,
  label,
  active = false,
  badge = 0,
  onClick,
  href,
  trailing,
  buttonRef,
  ariaExpanded,
  keepOpen,
}: SidebarItemProps) {
  const { expanded, isMobile, closeMobile } = usePortalSidebarPanel();
  const { anchor, handlers } = useTip(!expanded);
  const content = (
    <>
      <span className="psb-slot">
        <span className="psb-dotwrap">
          <Icon size={20} />
          {badge > 0 && !expanded && <span className="psb-dot">{badge > 9 ? '9+' : badge}</span>}
        </span>
      </span>
      {expanded && <span className="psb-label">{label}</span>}
      {expanded && badge > 0 && <span className="psb-badge">{badge > 99 ? '99+' : badge}</span>}
      {expanded && trailing}
    </>
  );
  const handleClick = () => {
    onClick?.();
    if (isMobile && !keepOpen) closeMobile();
  };
  const common = {
    className: 'psb-item',
    'data-active': active ? 'true' : 'false',
    'data-collapsed': expanded ? 'false' : 'true',
    'aria-label': expanded ? undefined : label,
    ...handlers,
  };
  return (
    <>
      {href ? (
        <a href={href} {...common} onClick={() => isMobile && !keepOpen && closeMobile()}>
          {content}
        </a>
      ) : (
        <button type="button" ref={buttonRef} aria-expanded={ariaExpanded} onClick={handleClick} {...common}>
          {content}
        </button>
      )}
      <Tip anchor={anchor} label={label} />
    </>
  );
}

/** Collapsible group of sub items. In the collapsed rail, clicking it expands the rail and opens the group. */
export function SidebarGroup({
  icon,
  label,
  active,
  open,
  onOpenChange,
  children,
}: {
  icon: PortalIcon;
  label: string;
  active?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  const { expanded, expandRail } = usePortalSidebarPanel();
  return (
    <>
      <SidebarItem
        icon={icon}
        label={label}
        active={active && !(expanded && open)}
        ariaExpanded={expanded ? open : false}
        keepOpen
        onClick={() => {
          if (!expanded) {
            expandRail();
            onOpenChange(true);
          } else {
            onOpenChange(!open);
          }
        }}
        trailing={<ChevronDownIcon size={16} className="psb-chev" />}
      />
      {expanded && (
        <div className="psb-subwrap" data-open={open ? 'true' : 'false'}>
          <div className="psb-sub">{children}</div>
        </div>
      )}
    </>
  );
}

export function SidebarSection({ label }: { label: string }) {
  const { expanded } = usePortalSidebarPanel();
  if (!expanded) return null;
  return <p className="psb-section">{label}</p>;
}

export function SidebarDivider() {
  return <div className="psb-divider" />;
}

/* ------------------------------------------------------------------ shell */

export type PortalSidebarProps = {
  brand: { icon: PortalIcon; eyebrow: string; title: string };
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  user?: { name?: string | null; email?: string | null };
  onLogout: () => void;
  /** Extra controls shown above Logout (expanded / mobile only). */
  footerExtra?: ReactNode;
  /** Extra controls on the right of the mobile top bar (e.g. a notifications bell). */
  mobileTopBarRight?: ReactNode;
  /** Navigation content (SidebarItem / SidebarGroup / SidebarSection ...). Put Home last. */
  children: ReactNode;
};

function initialsOf(name?: string | null, email?: string | null) {
  return (
    (name || email || 'U')
      .split(/\s+/)
      .map((part) => part.trim()[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'
  );
}

export function PortalSidebar({
  brand,
  expanded,
  onExpandedChange,
  mobileOpen,
  onMobileOpenChange,
  user,
  onLogout,
  footerExtra,
  mobileTopBarRight,
  children,
}: PortalSidebarProps) {
  useInterFont();
  const BrandIcon = brand.icon;
  const initials = initialsOf(user?.name, user?.email);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  const toggleFromBackground = (event: ReactMouseEvent<HTMLElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('a, button, input, select, textarea, label, [data-psb-no-toggle]')) return;
    onExpandedChange(!expandedRef.current);
  };

  const renderPanel = (isMobile: boolean) => {
    const wide = isMobile || expanded;
    const ctx: PanelCtx = {
      expanded: wide,
      isMobile,
      expandRail: () => onExpandedChange(true),
      closeMobile: () => onMobileOpenChange(false),
    };
    return (
      <PanelContext.Provider value={ctx}>
        <div
          className="psb-panel"
          data-clickable={isMobile ? 'false' : 'true'}
          onClick={isMobile ? undefined : toggleFromBackground}
        >
          <div className="psb-head" onClick={(e) => e.stopPropagation()}>
            {wide ? (
              <>
                <span className="psb-slot">
                  <span className="psb-plate">
                    <BrandIcon size={20} />
                  </span>
                </span>
                <div className="psb-brand">
                  <p className="psb-eyebrow">{brand.eyebrow}</p>
                  <p className="psb-title">{brand.title}</p>
                </div>
                {isMobile ? (
                  <button
                    type="button"
                    className="psb-iconbtn psb-close"
                    aria-label="Close menu"
                    onClick={() => onMobileOpenChange(false)}
                  >
                    <CloseIcon size={20} />
                  </button>
                ) : (
                  <CollapseButton onClick={() => onExpandedChange(false)} />
                )}
              </>
            ) : (
              <span className="psb-slot">
                <ExpandPlate icon={BrandIcon} onClick={() => onExpandedChange(true)} />
              </span>
            )}
          </div>

          <nav className="psb-nav">{children}</nav>

          <div className="psb-foot">
            <div className="psb-user">
              <span className="psb-slot">
                <span className="psb-avatar" title={!wide ? user?.name || undefined : undefined}>
                  {initials}
                </span>
              </span>
              {wide && (
                <div className="psb-who">
                  <p className="psb-name">{user?.name}</p>
                  <p className="psb-email">{user?.email}</p>
                </div>
              )}
            </div>
            {wide && footerExtra ? (
              <div className="psb-extra" data-psb-no-toggle onClick={(e) => e.stopPropagation()}>
                {footerExtra}
              </div>
            ) : null}
            <LogoutButton onLogout={onLogout} />
          </div>
        </div>
      </PanelContext.Provider>
    );
  };

  return (
    <div className="psb">
      <style>{CSS}</style>

      <header className="psb-topbar">
        <button
          type="button"
          className="psb-topbtn"
          aria-label="Open menu"
          onClick={() => onMobileOpenChange(true)}
        >
          <MenuIcon size={20} />
        </button>
        <div className="psb-topbrand">
          <span className="psb-topplate">
            <BrandIcon size={16} />
          </span>
          <span className="psb-toptitle">{brand.title}</span>
        </div>
        {mobileTopBarRight ? <div className="psb-topright">{mobileTopBarRight}</div> : null}
      </header>

      <aside className="psb-rail" data-collapsed={expanded ? 'false' : 'true'}>
        {renderPanel(false)}
      </aside>

      <div className="psb-drawer" data-open={mobileOpen ? 'true' : 'false'} aria-hidden={!mobileOpen}>
        <div className="psb-overlay" onClick={() => onMobileOpenChange(false)} />
        <aside className="psb-sheet">{renderPanel(true)}</aside>
      </div>
    </div>
  );
}

function CollapseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="psb-iconbtn"
      aria-label="Collapse sidebar"
      title="Collapse sidebar"
      onClick={onClick}
    >
      <PanelCloseIcon size={18} />
    </button>
  );
}

function ExpandPlate({ icon: Icon, onClick }: { icon: PortalIcon; onClick: () => void }) {
  const { anchor, handlers } = useTip(true);
  return (
    <>
      <button type="button" className="psb-plate" aria-label="Expand sidebar" onClick={onClick} {...handlers}>
        <span className="psb-brand-icon">
          <Icon size={20} />
        </span>
        <PanelOpenIcon size={20} className="psb-swap" />
      </button>
      <Tip anchor={anchor} label="Expand sidebar" />
    </>
  );
}

function LogoutButton({ onLogout }: { onLogout: () => void }) {
  const { expanded } = usePortalSidebarPanel();
  const { anchor, handlers } = useTip(!expanded);
  return (
    <>
      <button
        type="button"
        className="psb-item psb-logout"
        data-collapsed={expanded ? 'false' : 'true'}
        aria-label="Logout"
        onClick={onLogout}
        {...handlers}
      >
        <span className="psb-slot">
          <LogOutIcon size={20} />
        </span>
        {expanded && <span className="psb-label">Logout</span>}
      </button>
      <Tip anchor={anchor} label="Logout" />
    </>
  );
}

export default PortalSidebar;
